import { useContext, useEffect, useMemo, useRef } from "react"
import { DefaultValues, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import cloneDeep from "lodash-es/cloneDeep"

import { useEvent } from "shared/lib/useEvent"
import { RJSFFormData } from "shared/types/RJSF"

import { getDefaultFormData } from "entities/dim-configuration/lib/getDefaultFormData"

import { RJSFFormAPI, RJSFFormProps } from "../RJSFForm"
import { RJSFFormContext } from "../RJSFFormContext"
import { buildSchema } from "../yup/buildSchema"
import { useYupValidationResolver } from "./useYupValidationResolver"

export function useRJSFFormViewModel<T extends RJSFFormData>({
  id,
  name,
  schema,
  formData,
  submitOnChange = false,
  formAPIRef,
  onChange,
  onSubmit,
  ...rest
}: RJSFFormProps<T>) {
  const { t } = useTranslation()
  const lastChangedFieldRef = useRef<string | undefined>(undefined)
  const { registerForm } = useContext(RJSFFormContext)

  const defaultFormDataFromSchema = useMemo(
    () => getDefaultFormData(schema),
    [schema]
  )

  const yupSchema = useMemo(() => {
    const clonedSchema = cloneDeep(schema)
    return clonedSchema ? buildSchema(clonedSchema) : null
  }, [schema])

  const resolver = useYupValidationResolver(yupSchema)
  const methods = useForm<T>({
    defaultValues:
      (formData as DefaultValues<T>) ||
      (defaultFormDataFromSchema as DefaultValues<T>),
    resolver
  })

  const handleReset = useEvent((nextFormData?: RJSFFormData) => {
    methods.reset(
      (nextFormData as DefaultValues<T>) || defaultFormDataFromSchema
    )
  })

  const formAPI: RJSFFormAPI = useMemo(
    () => ({
      reset: handleReset
    }),
    [handleReset]
  )

  useEffect(() => {
    if (name) {
      registerForm(name, formAPI)
    }
    if (formAPIRef) {
      // eslint-disable-next-line no-param-reassign
      formAPIRef.current = formAPI
    }
  }, [id, name, formAPI])

  const handleSubmit = useEvent((nextFormData: T) => {
    onSubmit?.(nextFormData, lastChangedFieldRef.current)
  })

  useEffect(() => {
    const subscription = methods.watch(
      (nextFormData, { type, name: fieldName }) => {
        if (type === "change") {
          lastChangedFieldRef.current = fieldName
          onChange?.(nextFormData as T, fieldName)
          if (submitOnChange) {
            methods.handleSubmit(handleSubmit)()
          }
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [submitOnChange])

  const submitHandler = useMemo(
    () => methods.handleSubmit(handleSubmit),
    [methods.handleSubmit, handleSubmit]
  )

  useEffect(() => {
    if (formData) {
      methods.reset(formData as DefaultValues<T>)
    }
  }, [formData])

  const submitButtonText = rest.submitButtonText || t("General.Confirm")

  return {
    ...rest,
    id,
    schema,
    submitButtonText,
    onChange,
    onSubmit: submitHandler,
    methods
  }
}
